import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { useLeaveChildMutation, useUpdateChildMutation } from "./api/children.mutations"
import { ChildForm } from "./ChildForm"
import type { ChildFormValues } from "./children.schemas"
import { InviteSection } from "./InviteSection"
import { clearSelectedChild, useSelectedChild } from "./useSelectedChild"

export function EditChildPage({ childId }: { childId: string }) {
  const navigate = useNavigate()
  const { showApiError } = useErrorDialog()
  const { children } = useSelectedChild()
  const update = useUpdateChildMutation()
  const leave = useLeaveChildMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const child = children.find((c) => c.id === childId)

  if (!child) return null

  const onSubmit = (values: ChildFormValues) => {
    update.mutate(
      { id: childId, values },
      {
        onSuccess: () => navigate({ to: "/" }),
        onError: (err) => showApiError(err, { title: "Kunde inte spara" }),
      },
    )
  }

  const onLeave = () => {
    leave.mutate(childId, {
      onSuccess: () => {
        clearSelectedChild()
        navigate({ to: "/" })
      },
      onError: (err) => showApiError(err, { title: "Kunde inte ta bort barnprofil" }),
    })
  }

  return (
    <div className="flex flex-col gap-8 p-4">
      <section>
        <h1 className="mb-4 text-lg font-semibold">Redigera {child.name}</h1>
        <ChildForm
          defaultValues={{ name: child.name, birthDate: child.birthDate, sex: child.sex }}
          onSubmit={onSubmit}
          isPending={update.isPending}
          submitLabel="Spara"
        />
      </section>

      <InviteSection childId={childId} />

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">Riskzon</h2>
        <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
          Ta bort barnprofil
        </Button>
      </section>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ta bort {child.name}?</DialogTitle>
            <DialogDescription>
              Om du är den sista vårdnadshavaren raderas barnet och all sömndata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Avbryt</Button>
            </DialogClose>
            <Button variant="destructive" onClick={onLeave} isLoading={leave.isPending}>
              Ta bort barnprofil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
